import React, { useCallback, useMemo, useState } from "react";
import { Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Tab, Tabs, TextField, Typography, CircularProgress, Box } from "@mui/material";
import "@git-diff-view/react/styles/diff-view.css";
import { DiffView, DiffModeEnum } from "@git-diff-view/react";
import { generateDiffFile } from "@git-diff-view/file";
import { ReplicateIniChanges } from "system/types/interfaces";

interface DiffModalProps {
  open: boolean;
  onClose: () => void;
  onNext: (comment?: string) => void;
  originalText: string;
  newText: string;
  pendingChangesCount: number;
  canDownloadTheFile: boolean;
  saveLabel?: string;
  replicateOriginalText?: string;
  replicateGeneratedText?: string;
  pendingReplicateChanges?: ReplicateIniChanges;
}

const DiffModal: React.FC<DiffModalProps> = ({
  open,
  onClose,
  onNext,
  originalText,
  newText,
  pendingChangesCount,
  canDownloadTheFile,
  saveLabel,
  pendingReplicateChanges = { additions: [], removals: [] },
}) => {
  const [saveComment, setSaveComment] = useState("");
  const [tabIndex, setTabIndex] = useState<0 | 1>(0);
  const totalLibraryChanges = pendingReplicateChanges.additions.length + pendingReplicateChanges.removals.length;
  const hasLibraryChanges = totalLibraryChanges > 0;

  // generateDiffFile throws when the two texts match, so it cannot run before the guard below.
  const diffFile = useMemo(() => {
    if (!originalText || !newText || originalText === newText) return null;
    try {
      const instance = generateDiffFile("oldCompany.ini", originalText, "newCompany.ini", newText, "ini", "ini");
      instance.initRaw();
      return instance;
    } catch {
      return null;
    }
  }, [originalText, newText]);

  if (!originalText || !newText) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} sx={{ "& .MuiDialog-paper": { maxHeight: "90vh", width: "90vw" } }}>
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff", display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="inherit" fontWeight="bold" color="#ffffff">
            Review Changes
          </Typography>
          {pendingChangesCount > 0 && (
            <Typography variant="subtitle1" color="#fbc02d" sx={{ mt: 1 }}>
              <b>
                {pendingChangesCount} {pendingChangesCount === 1 ? "Change" : "Changes"}
              </b>
            </Typography>
          )}
        </Box>
        {hasLibraryChanges && (
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v as 0 | 1)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ "& .MuiTab-root": { color: "#fff", textTransform: "none" } }}
          >
            <Tab label="Policy" />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  Library
                  <Chip
                    label={`±${totalLibraryChanges}`}
                    size="small"
                    sx={{ backgroundColor: "rgba(0, 120, 212, 0.15)", color: "#4daafc" }}
                  />
                </Box>
              }
            />
          </Tabs>
        )}
        {tabIndex === 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" color="#ffffff" sx={{ padding: "8px", flex: 1, textAlign: "center" }}>
              {saveLabel ? "Current Version" : "Original"}
            </Typography>
            <Typography variant="h6" color="#ffffff" sx={{ padding: "8px", flex: 1, textAlign: "center" }}>
              {saveLabel ? "Your Changes" : "New"}
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#1b1a19",
          overflowY: "auto",
          maxHeight: "70vh",
          padding: 2,
          paddingTop: 0,
        }}
      >
        {tabIndex === 0 || !hasLibraryChanges ? (
          !diffFile ? (
            <Box sx={{ p: 4, textAlign: "center", color: "#ffffff" }}>
              <Typography>No changes detected in Company.ini.</Typography>
            </Box>
          ) : (
          <DiffView
            diffFile={diffFile}
            diffViewWrap={false}
            diffViewAddWidget
            diffViewMode={DiffModeEnum.Split}
            diffViewTheme="dark"
            diffViewHighlight={true}
            diffViewFontSize={12}
          />
          )
        ) : (
          <Box>
            {pendingReplicateChanges.additions.length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, border: "1px solid #2d4a2f", borderRadius: 1, backgroundColor: "#1f2b1f" }}>
                <Typography variant="subtitle2" sx={{ color: "#7fc97a", mb: 1, fontWeight: 600 }}>
                  Adding to Library ({pendingReplicateChanges.additions.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", color: "#F3F2F1" }}>
                  {pendingReplicateChanges.additions.map((p) => (
                    <li key={`add-${p}`}>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        <Box component="span" sx={{ color: "#7fc97a", mr: 1 }}>+</Box>
                        {p}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
            {pendingReplicateChanges.removals.length > 0 && (
              <Box sx={{ p: 1.5, border: "1px solid #4a2d2d", borderRadius: 1, backgroundColor: "#2b1f1f" }}>
                <Typography variant="subtitle2" sx={{ color: "#d5878b", mb: 1, fontWeight: 600 }}>
                  Removing from Library ({pendingReplicateChanges.removals.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", color: "#F3F2F1" }}>
                  {pendingReplicateChanges.removals.map((p) => (
                    <li key={`rm-${p}`}>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        <Box component="span" sx={{ color: "#d5878b", mr: 1 }}>−</Box>
                        {p}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19", padding: 2, gap: 1 }}>
        {saveLabel && (
          <TextField
            placeholder="Add a comment (optional)"
            size="small"
            value={saveComment}
            onChange={(e) => setSaveComment(e.target.value)}
            sx={{
              flex: 1,
              "& .MuiInputBase-root": { color: "#F3F2F1", fontSize: "13px" },
              "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#605e5c" }, "&:hover fieldset": { borderColor: "#F3F2F1" } },
            }}
          />
        )}
        <Button variant="outlined" onClick={onClose} sx={{ color: "#ffffff", borderColor: "#757575" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => { onNext(saveComment || undefined); setSaveComment(""); }} disabled={pendingChangesCount === 0}>
          {saveLabel ?? (canDownloadTheFile ? "Download" : "Next")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiffModal;
